import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import useCurrentCoachProfile from "app/utils/hooks/scheduling/useCurrentCoachProfile";
import { UserContext, RoleType } from "app/contexts/UserContext";
import { useSelectedCoach } from "app/contexts/SelectedCoachContext";

const Navbar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const currentCoachProfile = useCurrentCoachProfile();
  const location = useLocation();
  const user = useContext(UserContext);
  const { selectedCoachId, setSelectedCoachId } = useSelectedCoach();

  const handleTabClick = (path: string) => {
    history.push(path, { fromCalendarSettings: true });
  };

  const handleIntegrationsTabClick = () => {
    // Always switch back to own profile when clicking integrations tab
    // (coaches can only edit their own calendar integrations)
    const ownCoachProfileId = currentCoachProfile?.data?.id;
    if (ownCoachProfileId && selectedCoachId !== ownCoachProfileId) {
      setSelectedCoachId(ownCoachProfileId);
    }
    history.push("/calendar/settings", { fromCalendarSettings: true });
  };

  const showTabs = !currentCoachProfile?.data?.allowScheduleProviderAccess;
  const showIntegrationsTab = user.role === RoleType.COACH;
  // Hide Booking Options tab when facility-level booking options are enabled
  const facilityBookingEnabled =
    user.facility?.featureFlags?.facilityLevelBookingOptions;
  // Calendar Display holds per-user preferences. When an admin has selected
  // another coach, hide the tab so they can't edit settings that don't
  // belong to them.
  const ownCoachProfileId = currentCoachProfile?.data?.id;
  const isViewingAnotherCoach =
    !!ownCoachProfileId &&
    !!selectedCoachId &&
    selectedCoachId !== ownCoachProfileId;
  // Calendar Display is a per-coach personal preference: admins and
  // schedulers never see the tab, and even coaches hide it while acting on
  // another coach's behalf.
  const showDisplayTab = user.role === RoleType.COACH && !isViewingAnotherCoach;

  return (
    <nav className={styles.navbar}>
      <ul className={styles.navList}>
        {showTabs && (
          <>
            {!facilityBookingEnabled && (
              <li
                className={`${styles.navItem} ${
                  location.pathname.includes("/pricing") ? styles.active : ""
                }`}
                onClick={() => handleTabClick("/calendar/pricing")}
              >
                {t("Booking Options")}
              </li>
            )}
            <li
              className={`${styles.navItem} ${
                location.pathname.includes("/availability") ? styles.active : ""
              }`}
              onClick={() => handleTabClick("/calendar/availability")}
            >
              {t("Availability")}
            </li>
            <li
              className={`${styles.navItem} ${
                location.pathname.includes("/scheduling") ? styles.active : ""
              }`}
              onClick={() => handleTabClick("/calendar/scheduling")}
            >
              {t("Schedule Options")}
            </li>
          </>
        )}
        {showDisplayTab && (
          <li
            className={`${styles.navItem} ${
              location.pathname.includes("/display") ? styles.active : ""
            }`}
            onClick={() => handleTabClick("/calendar/display")}
          >
            {t("Calendar Display")}
          </li>
        )}
        {showIntegrationsTab && (
          <li
            className={`${styles.navItem} ${
              location.pathname.includes("/settings") ? styles.active : ""
            }`}
            onClick={handleIntegrationsTabClick}
          >
            {t("Calendar Integrations")}
          </li>
        )}
      </ul>
      <div className={styles.horizontalLine} />
    </nav>
  );
};

export default Navbar;
